import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { GiOpenTreasureChest } from 'react-icons/gi';
import BoardTreasureReveal from './BoardTreasureReveal';
import GuardianDamageCalculation from './GuardianDamageCalculation';
import RelicIcon from './RelicIcon';
import { getSectorMinion } from './sectorEnemies';
import { playSound } from '../../utils/utils';
import { WARDEN_ARRIVAL_SOUND } from '../../utils/soundEffects';
import GlitchPrismScene from './GlitchPrismScene';
import { getIceTileIndices } from './boardConditions';
import SlingshotLauncher from './SlingshotLauncher';
import CoreboundTip from './CoreboundTip';
import { useCoreboundHelp } from './CoreboundHelp';
import { formatDieValue, rollDie } from './dice';
import RelicAbilityBar from './RelicAbilityBar';
import { getBountyTagOutcome, getBountyTagState } from './bountyTag';
import {
  getBoardEffectTotal,
  getBoardRollSteps,
  hasChainReaction,
  getChainReactionState,
  getTriggeredBoardSquares,
} from './chainReaction';
import { getPortalPairState } from './portalPair';
import { getPhaseAnchorState } from './phaseAnchor';
import MirrorChipActivation from './MirrorChipActivation';
import { BoardEffectTile, isBoardEffectTile } from './BoardEffectIcons';
import BoardTileChoice from './BoardTileChoice';
import { getPendingBoardTile, needsBoardTileChoice } from './boardTileEffects';
import { BoardTileRelayCell, BoardTileRelayPicker } from './BoardTileRelay';
import { getBoardRelaunchRequest } from './powerUp';
import {
  getBaseRoll,
  getMaxHp,
  getBoardTreasureRewards,
  getBoardAdjustedRoll,
  getRollRelicEffects,
  needsMirrorActivation,
  reverseRollDigits,
} from './game';
import { RELICS } from './relics';
import { TREASURE_TILE_CHARGE_MS } from './treasureReveal';
import useBreachStrike from './useBreachStrike';
import {
  getBoardSquare,
  getBoardSquareBackgroundColor,
  getBoardSquareColor,
  getBoardSquareImpactLabel,
  getBoardSquareLandingBackgroundColor,
  getBoardSquareLandingTextColor,
  getBoardSquareLabel,
  getBoardSquareTextColor,
} from './doorBoard';

const TICK_DELAYS = [70, 80, 90, 100, 120, 150, 190, 240, 300, 390];
const TICK_TOTAL = TICK_DELAYS.reduce((total, delay) => total + delay, 0);
const MINION_ARRIVAL_SOUNDS = [
  'minion-appears.mp3',
  'minion-appears-2.mp3',
  'minion-appears-3.mp3',
  'minion-appears-4.mp3',
  'minion-appears-6.mp3',
  'minion-appears-7.mp3',
  'minion-appears-8.mp3',
  'minion-appears-9.mp3',
];
const formatRoll = formatDieValue;

const RollReveal = ({
  game,
  room,
  effectiveRoll,
  onFlip,
  onBoardTileChoice,
  onResolve,
  setup = false,
  arrival = setup,
  onArrivalComplete,
  onStartRoll,
  onSelectDie,
  onRollComplete,
  onMarkBounty,
  onClearBounty,
  onToggleChainReaction,
  onPhaseAnchor,
  onPortalTransit,
  onBoardBump,
  rollCharge = 0.72,
  rollLaunch = null,
  restoreCompletedRoll = false,
  survival = false,
  encounter = null,
  lastAttack = null,
}) => {
  const { isTipVisible } = useCoreboundHelp();
  const setupMode = setup;
  const [displayRoll, setDisplayRoll] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [rollReady, setRollReady] = useState(false);
  const [physicsComplete, setPhysicsComplete] = useState(false);
  const [autoRelicsApplied, setAutoRelicsApplied] = useState(false);
  const [boardApplied, setBoardApplied] = useState(false);
  const [boardPowering, setBoardPowering] = useState(false);
  const [boardShine, setBoardShine] = useState(false);
  const [relicStage, setRelicStage] = useState('idle');
  const relicPowering = relicStage === 'charging';
  const relicShine = relicStage === 'shining';
  const [boardRollDisplay, setBoardRollDisplay] = useState(null);
  const [activeBoardIndex, setActiveBoardIndex] = useState(null);
  const [relicRollDisplay, setRelicRollDisplay] = useState(null);
  const [treasureRevealStage, setTreasureRevealStage] = useState(0);
  const [treasureRewardIndex, setTreasureRewardIndex] = useState(0);
  const [mirrorApplied, setMirrorApplied] = useState(false);
  const [mirrorPowering, setMirrorPowering] = useState(false);
  const [mirrorChipAnimating, setMirrorChipAnimating] = useState(false);
  const mirrorActivationRef = useRef(null);
  const gameRollRef = useRef(game.roll);
  gameRollRef.current = game.roll;
  const restoredRoll = restoreCompletedRoll && !setupMode;
  const reduceMotion = !!window.matchMedia?.('(prefers-reduced-motion: reduce)')
    .matches;
  const rollRelicEffects = getRollRelicEffects(game, room);
  const relicEffectSignature = rollRelicEffects
    .map((effect) => effect.id)
    .join('|');
  const treasureRewards = getBoardTreasureRewards(game);
  const boardTreasureReward = treasureRewards[treasureRewardIndex];
  const treasureRelic = boardTreasureReward
    ? RELICS[boardTreasureReward.id]
    : null;
  const treasureActivation = boardTreasureReward
    ? rollRelicEffects.find((effect) => effect.id === boardTreasureReward.id)
    : null;
  const baseRoll = game.roll;
  const initialRoll = getBaseRoll(game);
  const calculationKey = `${game.roll}:${game.landingIndex}:${game.flipped}`;
  const calculationKeyRef = useRef(null);
  const calculationsCurrent = calculationKeyRef.current === calculationKey;
  const bonusTotal = rollRelicEffects.reduce(
    (total, effect) => total + effect.bonus,
    0
  );
  const introThrow = game.breachIntro && game.floor === 1;
  const {
    combat: breachCombat,
    start: startBreachStrike,
    onCombatReady: onBreachCombatReady,
  } = useBreachStrike({
    enabled: introThrow && !survival,
    game,
    effectiveRoll,
    reduceMotion,
    onResolve,
  });
  const sectorEncounter = survival
    ? null
    : encounter || {
        enemy: getSectorMinion(game.floor),
        hp: room.target,
        training: introThrow,
        automatic: introThrow,
        combat: breachCombat,
        onCombatReady: onBreachCombatReady,
      };
  const arrivalCueKeyRef = useRef(null);
  useEffect(() => {
    if (
      !setupMode ||
      !arrival ||
      sectorEncounter?.training ||
      (encounter?.automatic && game.guardian?.round !== 1)
    )
      return;
    const enemyId = sectorEncounter?.enemy?.id;
    const cueKey = `${game.floor}:${game.roomId}:${enemyId}`;
    if (arrivalCueKeyRef.current === cueKey) return;
    arrivalCueKeyRef.current = cueKey;
    const sound =
      encounter?.boss || enemyId === 'rift-warden'
        ? WARDEN_ARRIVAL_SOUND
        : `/sounds/${
            MINION_ARRIVAL_SOUNDS[
              Math.floor(Math.random() * MINION_ARRIVAL_SOUNDS.length)
            ]
          }`;
    playSound(sound, { preventOverlap: true });
  }, [
    setupMode,
    arrival,
    sectorEncounter?.training,
    sectorEncounter?.enemy?.id,
    encounter?.automatic,
    encounter?.boss,
    game.floor,
    game.roomId,
    game.guardian?.round,
  ]);
  const passed =
    introThrow || (effectiveRoll !== null && effectiveRoll >= room.target);
  const outcomeStatus = introThrow
    ? 'INTRO COMPLETE'
    : survival
    ? passed
      ? 'SURVIVED'
      : 'FAILED'
    : encounter?.boss
    ? passed
      ? 'WARDEN BROKEN'
      : 'STRIKE READY'
    : passed
    ? 'ONE SHOT'
    : 'MINION SURVIVES';
  const bountyTagIndex = survival ? null : getBountyTagState(game).targetIndex;
  const bountyOutcome = survival ? null : getBountyTagOutcome(game, passed);
  const boardAdjustedRoll = getBoardAdjustedRoll(game);
  const chainActive = hasChainReaction(game);
  const boardSteps = getBoardRollSteps(game, initialRoll);
  const boardStepsSignature = boardSteps
    .map(
      ({ index, square, before, value }) =>
        `${index}:${before}:${value}:${JSON.stringify(square)}`
    )
    .join('|');
  const boardStepsRef = useRef(boardSteps);
  boardStepsRef.current = boardSteps;
  const activeBoardStep = boardSteps.find(
    ({ index }) => index === activeBoardIndex
  );
  const activeBoardSquare =
    activeBoardStep?.square ||
    getBoardSquare(game.doorBoard, game.landingIndex);
  const relaunchRequest = !setupMode ? getBoardRelaunchRequest(game) : null;
  const tileChoicePending =
    !setupMode && !relaunchRequest && needsBoardTileChoice(game);
  const pendingBoardTile = tileChoicePending ? getPendingBoardTile(game) : null;
  const relayChoicePending =
    pendingBoardTile?.type === 'relay' ? pendingBoardTile : null;
  const waitingForChainCalculation =
    !!relaunchRequest?.afterChain && !boardApplied;
  const activeRelaunch = waitingForChainCalculation ? null : relaunchRequest;
  const mirrorActivationPending =
    !setupMode && !relaunchRequest && needsMirrorActivation(game);
  const mirrorFromChest =
    mirrorActivationPending &&
    treasureRewards.some(({ id }) => id === 'mirrorChip');
  const treasureCharging =
    revealed &&
    treasureRelic &&
    treasureRevealStage === 0 &&
    ((mirrorApplied && !mirrorActivationPending) || mirrorFromChest);
  const treasureReady =
    treasureRewards.length === 0 || treasureRevealStage >= 2;
  const effectsReady =
    !relaunchRequest &&
    !tileChoicePending &&
    !mirrorActivationPending &&
    !mirrorChipAnimating &&
    calculationsCurrent &&
    boardApplied &&
    treasureReady &&
    (rollRelicEffects.length === 0 || autoRelicsApplied);
  const resolveRef = useRef(onResolve);
  resolveRef.current = onResolve;
  const automaticAttack = !!sectorEncounter?.automatic;
  const combat = sectorEncounter?.combat;
  const explainingLanding = !survival && isTipVisible('landing');
  const firedCalculationRef = useRef(null);
  useEffect(() => {
    if (
      !automaticAttack ||
      setupMode ||
      !revealed ||
      !effectsReady ||
      explainingLanding ||
      combat ||
      firedCalculationRef.current === calculationKey
    )
      return undefined;
    firedCalculationRef.current = calculationKey;
    if (introThrow) startBreachStrike();
    else resolveRef.current?.();
  }, [
    automaticAttack,
    setupMode,
    revealed,
    effectsReady,
    explainingLanding,
    combat,
    calculationKey,
    introThrow,
    startBreachStrike,
  ]);
  useEffect(() => {
    if (!mirrorActivationPending) mirrorActivationRef.current = null;
  }, [mirrorActivationPending]);

  useEffect(() => {
    if (
      setupMode ||
      !revealed ||
      !mirrorActivationPending ||
      (mirrorFromChest && !treasureReady)
    )
      return;
    const activationId = `${game.floor}:${game.roomId}:${game.roll}:${game.landingIndex}`;
    if (mirrorActivationRef.current === activationId) return;
    mirrorActivationRef.current = activationId;
    playSound('/sounds/digital-technology-beep.mp3');
    onFlip?.();
  }, [
    setupMode,
    revealed,
    mirrorActivationPending,
    mirrorFromChest,
    treasureReady,
    game.floor,
    game.roomId,
    game.roll,
    game.landingIndex,
    onFlip,
  ]);

  useEffect(() => {
    if (!revealed || !game.flipped) {
      setMirrorChipAnimating(false);
      return undefined;
    }
    setMirrorChipAnimating(true);
    const timer = setTimeout(
      () => setMirrorChipAnimating(false),
      reduceMotion ? 450 : 850
    );
    return () => clearTimeout(timer);
  }, [revealed, game.flipped, game.roll, reduceMotion]);

  useEffect(() => {
    if (setupMode || !revealed || !effectsReady || passed || encounter?.boss)
      return;
    playSound('/sounds/futuristic_spinning.mp3');
  }, [setupMode, revealed, effectsReady, passed, encounter?.boss]);
  const visibleRoll = combat
    ? effectiveRoll
    : activeRelaunch
    ? relaunchRequest.before
    : revealed && (tileChoicePending || mirrorActivationPending)
    ? game.roll
    : revealed && !mirrorApplied
    ? getBaseRoll(game)
    : (revealed || restoredRoll) && !calculationsCurrent
    ? initialRoll
    : (relicPowering || relicShine) && Number.isFinite(relicRollDisplay)
    ? relicRollDisplay
    : (restoredRoll || revealed) && autoRelicsApplied
    ? effectiveRoll
    : revealed && Number.isInteger(game.landingIndex)
    ? boardApplied
      ? boardAdjustedRoll
      : boardRollDisplay ?? game.roll
    : (restoredRoll || revealed) && boardApplied
    ? boardAdjustedRoll
    : restoredRoll
    ? game.roll
    : displayRoll;
  const rollQuality = setupMode
    ? 0
    : Math.max(game.roll || 0, effectsReady ? effectiveRoll || 0 : 0);
  const isCriticalRoll = !setupMode && rollQuality >= 90;
  const criticalProgress = Math.max(0, Math.min(1, (rollQuality - 90) / 10));
  const animationCriticalRef = useRef(isCriticalRoll);
  const animationProgressRef = useRef(criticalProgress);
  const animationCritical = animationCriticalRef.current;
  const animationDuration = animationCritical
    ? 2600 + animationProgressRef.current * 2200
    : 2200;
  const cinematicRoll =
    !!relaunchRequest ||
    (!setupMode && !revealed && !restoredRoll) ||
    (automaticAttack && !setupMode && !tileChoicePending);

  useEffect(() => {
    setAutoRelicsApplied(false);
    setDisplayRoll(null);
    setRevealed(false);
    setRollReady(false);
    setPhysicsComplete(false);
    if (setupMode) return undefined;
    if (restoredRoll) {
      setDisplayRoll(gameRollRef.current);
      setRollReady(true);
      setPhysicsComplete(true);
      setRevealed(true);
      return undefined;
    }
    // The WebGL scene is not rendered in Jest; production still waits for the
    // physical cube to report that it has fully settled.
    if (process.env.NODE_ENV === 'test') setPhysicsComplete(true);
    if (reduceMotion) {
      if (gameRollRef.current !== null) setDisplayRoll(gameRollRef.current);
      setRollReady(true);
      if (gameRollRef.current !== null) setPhysicsComplete(true);
      return undefined;
    }

    let elapsed = 0;
    const scheduledTimers = [];
    const finalPause = animationCritical ? 620 : 280;
    const tickScale = (animationDuration - finalPause) / TICK_TOTAL;
    TICK_DELAYS.forEach((delay) => {
      elapsed += delay * tickScale;
      scheduledTimers.push(
        setTimeout(() => {
          setDisplayRoll(rollDie(game.selectedDie));
        }, elapsed)
      );
    });
    scheduledTimers.push(
      setTimeout(() => {
        if (gameRollRef.current !== null) setDisplayRoll(gameRollRef.current);
        setRollReady(true);
      }, elapsed + finalPause)
    );

    return () => scheduledTimers.forEach(clearTimeout);
  }, [
    reduceMotion,
    setupMode,
    restoredRoll,
    animationCritical,
    animationDuration,
    game.selectedDie,
  ]);

  useEffect(() => {
    if (rollReady && physicsComplete) setRevealed(true);
  }, [rollReady, physicsComplete]);

  useEffect(() => {
    setMirrorApplied(false);
    setMirrorPowering(false);
    if (setupMode || !revealed || tileChoicePending || mirrorActivationPending)
      return undefined;
    if (!game.flipped || reduceMotion) {
      setMirrorApplied(true);
      return undefined;
    }
    setMirrorPowering(true);
    const timer = setTimeout(() => {
      setMirrorPowering(false);
      setMirrorApplied(true);
    }, 850);
    return () => clearTimeout(timer);
  }, [
    setupMode,
    revealed,
    mirrorActivationPending,
    tileChoicePending,
    game.flipped,
    game.roll,
    reduceMotion,
  ]);

  useEffect(() => {
    setTreasureRevealStage(0);
    setTreasureRewardIndex(0);
  }, [setupMode, game.roomId, game.floor, game.roll, game.landingIndex]);

  useEffect(() => {
    if (!revealed || !treasureRelic || treasureRevealStage !== 0)
      return undefined;
    if ((mirrorApplied && !mirrorActivationPending) || mirrorFromChest) {
      const timer = setTimeout(
        () => setTreasureRevealStage(1),
        TREASURE_TILE_CHARGE_MS
      );
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [
    revealed,
    treasureRelic,
    treasureRevealStage,
    mirrorApplied,
    mirrorActivationPending,
    mirrorFromChest,
    treasureRewardIndex,
  ]);

  useEffect(() => {
    calculationKeyRef.current = calculationKey;
    setBoardApplied(false);
    setAutoRelicsApplied(false);
    setBoardPowering(false);
    setBoardShine(false);
    setBoardRollDisplay(initialRoll);
    setActiveBoardIndex(null);
    setRelicStage('idle');
    setRelicRollDisplay(null);
    if (
      setupMode ||
      !revealed ||
      tileChoicePending ||
      mirrorActivationPending ||
      mirrorChipAnimating ||
      !mirrorApplied ||
      !treasureReady
    )
      return undefined;
    if (!Number.isInteger(game.landingIndex)) {
      setBoardRollDisplay(boardAdjustedRoll);
      setBoardApplied(true);
      return undefined;
    }
    if (reduceMotion) {
      setBoardRollDisplay(boardAdjustedRoll);
      setBoardApplied(true);
      return undefined;
    }

    const timers = [];
    let animationFrame;
    let cancelled = false;
    const steps = boardStepsRef.current;
    const applyStep = (position) => {
      const step = steps[position];
      if (!step) {
        setBoardRollDisplay(boardAdjustedRoll);
        setBoardApplied(true);
        return;
      }
      const activateStep = () => {
        if (cancelled) return;
        setActiveBoardIndex(step.index);
        setBoardPowering(true);
        if (!chainActive) playSound('/sounds/spell-casting.mp3');
        timers.push(
          setTimeout(
            () => {
              const startedAt = performance.now();
              const countDuration = chainActive ? 220 : 1050;
              const countUp = (now) => {
                if (cancelled) return;
                const progress = Math.min(1, (now - startedAt) / countDuration);
                const eased = 1 - (1 - progress) ** 3;
                setBoardRollDisplay(
                  Math.round(step.before + (step.value - step.before) * eased)
                );
                if (progress < 1) {
                  animationFrame = requestAnimationFrame(countUp);
                  return;
                }
                setBoardRollDisplay(step.value);
                setBoardPowering(false);
                setBoardShine(true);
                timers.push(
                  setTimeout(
                    () => {
                      if (cancelled) return;
                      setBoardShine(false);
                      if (position + 1 < steps.length) applyStep(position + 1);
                      else setBoardApplied(true);
                    },
                    chainActive ? 60 : 700
                  )
                );
              };
              animationFrame = requestAnimationFrame(countUp);
            },
            chainActive ? 60 : 720
          )
        );
      };
      if (chainActive) {
        playSound('/sounds/spell-casting.mp3', {
          preventOverlap: true,
        });
      }
      activateStep();
    };
    timers.push(setTimeout(() => applyStep(0), 240));
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [
    setupMode,
    revealed,
    game.landingIndex,
    game.roll,
    game.boardModifier,
    boardAdjustedRoll,
    initialRoll,
    calculationKey,
    reduceMotion,
    restoredRoll,
    tileChoicePending,
    mirrorActivationPending,
    mirrorChipAnimating,
    mirrorApplied,
    treasureReady,
    chainActive,
    boardStepsSignature,
  ]);

  useEffect(() => {
    if (!revealed || !calculationsCurrent || !boardApplied || tileChoicePending)
      return undefined;
    if (boardTreasureReward && treasureRelic && treasureRevealStage < 2)
      return undefined;
    const hasRelicActivation = !!relicEffectSignature || !!boardTreasureReward;
    const startRoll = boardAdjustedRoll ?? game.roll;
    const targetRoll = effectiveRoll ?? startRoll;
    if (reduceMotion) {
      setRelicRollDisplay(targetRoll);
      setAutoRelicsApplied(true);
      return undefined;
    }

    let animationFrame;
    let countTimer;
    let shineTimer;
    const effectTimer = setTimeout(
      () => {
        if (!hasRelicActivation) {
          setAutoRelicsApplied(true);
          return;
        }
        setAutoRelicsApplied(false);
        setRelicRollDisplay(startRoll);
        setRelicStage('charging');
        playSound('/sounds/spell-casting.mp3');
        const countStartedAt = performance.now();
        const countDuration = 1050;
        const countUp = (now) => {
          const progress = Math.min(1, (now - countStartedAt) / countDuration);
          const eased = 1 - (1 - progress) ** 3;
          setRelicRollDisplay(
            Math.round(startRoll + (targetRoll - startRoll) * eased)
          );
          if (progress < 1) {
            animationFrame = requestAnimationFrame(countUp);
            return;
          }
          setRelicRollDisplay(targetRoll);
          // Keep the popup mounted across the animation-frame transition.
          setRelicStage('shining');
          shineTimer = setTimeout(() => {
            setRelicStage('idle');
            setAutoRelicsApplied(true);
          }, 700);
        };
        countTimer = setTimeout(() => {
          animationFrame = requestAnimationFrame(countUp);
        }, 120);
      },
      hasRelicActivation ? 160 : 450
    );
    return () => {
      clearTimeout(effectTimer);
      clearTimeout(countTimer);
      clearTimeout(shineTimer);
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [
    revealed,
    boardApplied,
    calculationsCurrent,
    tileChoicePending,
    reduceMotion,
    relicEffectSignature,
    boardTreasureReward,
    treasureRelic,
    treasureRevealStage,
    game.flipped,
    game.roll,
    boardAdjustedRoll,
    effectiveRoll,
  ]);

  return (
    <div
      className={`coreboundPanel coreboundRollPanel ${
        game.flipped ? 'coreboundRelicUsed' : ''
      } ${autoRelicsApplied && !game.flipped ? 'coreboundAutoRelicUsed' : ''} ${
        setupMode ? 'coreboundRollSetup' : ''
      } ${cinematicRoll ? 'coreboundRollCinematic' : ''} ${
        automaticAttack && !setupMode ? 'coreboundGuardianThrow' : ''
      }`}
    >
      <span className="coreboundPanelKicker">
        AREA {game.floor} / {room.name.toUpperCase()}
      </span>
      <h2>
        {setupMode
          ? game.breachIntro
            ? 'Your first throw'
            : 'Aim your die'
          : relaunchRequest
          ? relaunchRequest.type === 'gambit'
            ? 'GAMBIT · Reroll incoming!'
            : 'POWER UP · Energy charging!'
          : revealed
          ? isCriticalRoll
            ? 'High roll captured!'
            : 'Your roll is in'
          : isCriticalRoll
          ? 'High roll detected…'
          : 'Revealing your roll…'}
      </h2>
      {setupMode && game.breachIntro && (
        <div className="coreboundBreachHint">
          <strong className="coreboundBreachHintTitle">
            STARTER THROW · NO PASS OR FAIL
          </strong>
          <p>Pick a reward tile. This throw is just to learn the board.</p>
          <div className="coreboundBreachRewards">
            <span>
              <strong>✦ Shards</strong>
              <small>Shop currency</small>
            </span>
            <span>
              <strong>＋ Repair</strong>
              <small>Restore Max HP</small>
            </span>
            <span>
              <strong>♜ Chests</strong>
              <small>Free relic</small>
            </span>
          </div>
        </div>
      )}
      <span className="coreboundSrOnly" role="status">
        {setupMode
          ? 'Roll setup is waiting for interaction.'
          : relaunchRequest
          ? relaunchRequest.type === 'gambit'
            ? `Gambit rerolls ${relaunchRequest.before} to ${relaunchRequest.after}. The die is relaunching.`
            : `Power Up boosts ${relaunchRequest.before} to ${relaunchRequest.after}. The die is relaunching.`
          : tileChoicePending
          ? `Natural roll ${formatRoll(
              game.roll
            )}. Choose your tile effect before calculations.`
          : revealed && mirrorActivationPending
          ? `Natural roll ${formatRoll(
              game.roll
            )}. Mirror Chip is automatically reversing it to ${formatRoll(
              reverseRollDigits(game.roll)
            )} because that is higher.`
          : revealed
          ? introThrow
            ? `First throw complete. Final roll ${formatRoll(
                effectiveRoll
              )}. Your landing reward is ready.`
            : `Natural roll ${formatRoll(game.roll)}. Final roll ${formatRoll(
                effectiveRoll
              )}. Enemy health ${room.target} HP.`
          : 'Roll animation in progress.'}
      </span>
      <div className="coreboundRollPresentation">
        {!setupMode && !survival && revealed && effectsReady && !combat && (
          <CoreboundTip
            id="landing"
            title="Your landing becomes your attack"
            floating
            action="Continue"
          >
            <p>
              The die landed on{' '}
              {getBoardSquareLabel(
                getBoardSquare(game.doorBoard, game.landingIndex)
              )}
              . Resolve its tile effect, then apply relic bonuses to get the
              final roll.
            </p>
            <p>
              Die {formatRoll(game.roll)}
              {game.flipped
                ? ` → Mirror Chip ${formatRoll(initialRoll)}`
                : ''}{' '}
              → after tiles {formatRoll(boardAdjustedRoll)} → final{' '}
              {formatRoll(effectiveRoll)}. Your final roll becomes damage
              against a guardian.
              {introThrow &&
                ' The Breach is a safe starter throw that also awards your landing reward.'}
            </p>
          </CoreboundTip>
        )}
        <div className="coreboundRollScene">
          {setupMode ? (
            <SlingshotLauncher
              sector={game.floor}
              relics={game.relics}
              playerHealth={{ hp: game.hp, maxHp: getMaxHp(game) }}
              aimTitle={
                survival
                  ? 'Last chance · Roll 76+'
                  : game.breachIntro
                  ? 'The Breach'
                  : `${room.name}`
              }
              encounter={sectorEncounter}
              aimDescription={
                survival
                  ? 'Your relic is keeping you alive. Score 76+ to survive with 1 HP.'
                  : null
              }
              arrival={arrival}
              onArrivalComplete={onArrivalComplete}
              attackRecap={
                lastAttack && (
                  <GuardianDamageCalculation
                    key={lastAttack.combatId}
                    result={lastAttack}
                  />
                )
              }
              selectedDie={game.selectedDie}
              onSelectDie={onSelectDie}
              doorBoard={game.doorBoard}
              tileInfoContext={{
                shards: game.shards,
              }}
              boardCondition={game.boardCondition}
              portalPair={getPortalPairState(game)}
              bountyTagIndex={bountyTagIndex}
              chainReactionReady={getChainReactionState(game).armed}
              relicControls={
                <RelicAbilityBar
                  game={game}
                  context={survival ? 'survival' : 'setup'}
                  onMarkBounty={onMarkBounty}
                  onClearBounty={onClearBounty}
                  onToggleChainReaction={onToggleChainReaction}
                />
              }
              onLaunch={(launch) => {
                playSound('/sounds/dice-roll-throw.mp3');
                onStartRoll?.(launch);
              }}
            />
          ) : (
            <GlitchPrismScene
              sector={game.floor}
              encounter={sectorEncounter}
              playerHealth={{ hp: game.hp, maxHp: getMaxHp(game) }}
              dieType={game.selectedDie}
              phase={
                activeRelaunch
                  ? 'rolling'
                  : setupMode
                  ? 'setup'
                  : restoredRoll || revealed
                  ? 'revealed'
                  : 'rolling'
              }
              charge={rollCharge}
              relaunch={activeRelaunch}
              trapEffect={
                !setupMode && (revealed || restoredRoll)
                  ? getTriggeredBoardSquares(game).find(
                      ({ square }) => square.effect?.type === 'trap'
                    )?.square.effect
                  : null
              }
              shieldAmount={
                !setupMode &&
                (revealed || restoredRoll) &&
                !tileChoicePending &&
                !relaunchRequest
                  ? getBoardEffectTotal(game, 'shield')
                  : 0
              }
              launch={rollLaunch}
              rollLabel={formatRoll(visibleRoll)}
              critical={isCriticalRoll}
              flipped={game.flipped}
              rollReady={rollReady}
              rollValue={game.roll}
              powerUp={game.powerUp}
              settledLandingIndex={game.landingIndex}
              treasureChargeIndex={
                treasureCharging
                  ? boardTreasureReward.index ?? game.landingIndex
                  : null
              }
              focusSettledDie={
                !combat && !relaunchRequest && (revealed || restoredRoll)
              }
              doorBoard={game.doorBoard}
              boardCondition={game.boardCondition}
              allowBumps={!setupMode && !restoredRoll}
              phaseAnchorArmed={
                !restoredRoll && getPhaseAnchorState(game).available
              }
              onPhaseAnchor={onPhaseAnchor}
              portalPair={getPortalPairState(game)}
              onPortalTransit={onPortalTransit}
              boardPowering={!combat && boardPowering}
              boardActiveIndex={activeBoardIndex}
              chainReactionTiles={
                chainActive ? boardSteps.map(({ index }) => index) : null
              }
              bountyTagIndex={bountyTagIndex}
              boardShine={!combat && boardShine}
              onBoardBump={onBoardBump}
              relicPowering={!combat && (mirrorPowering || relicPowering)}
              relicShine={!combat && relicShine}
              onImpact={() =>
                playSound('/sounds/low-frequency-dice-impact.mp3')
              }
              onAnimationComplete={(
                finalRoll,
                landingIndex,
                relaunchId,
                faceValues
              ) => {
                if (Number.isInteger(finalRoll)) {
                  setDisplayRoll(finalRoll);
                  onRollComplete?.(
                    finalRoll,
                    landingIndex,
                    relaunchId,
                    faceValues
                  );
                }
                setPhysicsComplete(true);
              }}
            />
          )}
        </div>
        {automaticAttack && !combat && (relicPowering || relicShine) && (
          <div
            className="coreboundRelicChargeFeed"
            role="status"
            aria-label="Relics charging the die"
          >
            <small>RELIC SURGE</small>
            <div>
              {rollRelicEffects.map((effect, index) => (
                <span
                  key={effect.id}
                  style={{
                    '--relic-delay': `${
                      index * Math.min(140, 800 / rollRelicEffects.length)
                    }ms`,
                  }}
                >
                  <RelicIcon relicId={effect.id} icon={effect.icon} />
                  <span>{effect.name}</span>
                </span>
              ))}
            </div>
          </div>
        )}
        {!combat && mirrorChipAnimating && (
          <MirrorChipActivation
            before={game.roll}
            after={getBaseRoll(game)}
            reduceMotion={reduceMotion}
          />
        )}
      </div>
      {!setupMode && relayChoicePending && (
        <BoardTileRelayPicker
          board={game.doorBoard}
          relay={relayChoicePending}
          onChoose={onBoardTileChoice}
        />
      )}
      {!setupMode &&
        Array.isArray(game.doorBoard) &&
        !automaticAttack &&
        !relayChoicePending && (
          <>
            <div
              className="coreboundDoorBoardPreview"
              role="grid"
              aria-label="Randomized door board"
            >
              {game.doorBoard.map((square, index) => {
                const squareLabel = getBoardSquareLabel(square);
                const isLanded = game.landingIndex === index;
                const className = [
                  'coreboundDoorBoardSquare',
                  isLanded && 'coreboundDoorBoardSquare-landed',
                  chainActive &&
                    activeBoardIndex === index &&
                    'is-chain-active',
                  bountyTagIndex === index && 'is-bounty-tag',
                  getIceTileIndices(game.boardCondition).includes(index) &&
                    'is-icy-tile',
                ]
                  .filter(Boolean)
                  .join(' ');
                const ariaLabel = [
                  bountyTagIndex === index ? 'Bounty target.' : '',
                  isLanded
                    ? 'Landed on square ' + (index + 1) + ': ' + squareLabel
                    : 'Square ' + (index + 1) + ': ' + squareLabel,
                ]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <BoardTileRelayCell
                    key={index}
                    index={index}
                    label={squareLabel}
                    className={className}
                    style={{
                      '--square-color': getBoardSquareColor(square),
                      '--landing-background-color':
                        getBoardSquareLandingBackgroundColor(square),
                      '--landing-text-color':
                        getBoardSquareLandingTextColor(square),
                      '--impact-text-color': getBoardSquareTextColor(square),
                    }}
                    current={isLanded}
                    ariaLabel={ariaLabel}
                  >
                    {bountyTagIndex === index && (
                      <small className="coreboundBountyTileLabel">
                        ⌖ BOUNTY
                      </small>
                    )}
                    {isLanded ? (
                      <>
                        <small className="coreboundDoorBoardLandedTag">
                          LANDED
                        </small>
                        {square.effect?.type === 'relic' && (
                          <GiOpenTreasureChest
                            className="coreboundDoorBoardTreasureIcon"
                            aria-hidden="true"
                          />
                        )}
                        {isBoardEffectTile(square) ? (
                          <BoardEffectTile effect={square.effect} />
                        ) : (
                          <span>{squareLabel}</span>
                        )}
                      </>
                    ) : square.effect?.type === 'relic' ? (
                      <>
                        <GiOpenTreasureChest
                          className="coreboundDoorBoardTreasureIcon"
                          aria-hidden="true"
                        />
                        <span>{squareLabel}</span>
                      </>
                    ) : isBoardEffectTile(square) ? (
                      <BoardEffectTile effect={square.effect} />
                    ) : (
                      squareLabel
                    )}
                  </BoardTileRelayCell>
                );
              })}
            </div>
          </>
        )}
      {revealed && !setupMode && !survival && !relayChoicePending && (
        <BoardTileChoice
          game={game}
          onChoose={onBoardTileChoice}
          reduceMotion={reduceMotion}
        />
      )}
      {revealed && chainActive && (
        <p className="coreboundChainSummary" role="status">
          CHAIN REACTION · {boardSteps.length} tiles · Landing → up → right →
          down → left
        </p>
      )}
      {revealed &&
        !combat &&
        mirrorApplied &&
        treasureReady &&
        !mirrorActivationPending &&
        !mirrorChipAnimating &&
        Number.isInteger(game.landingIndex) &&
        !boardApplied && (
          <motion.div
            className="coreboundBoardLanding"
            role="status"
            initial={reduceMotion ? false : { opacity: 0, y: 12, scale: 0.85 }}
            animate={
              reduceMotion ? {} : { opacity: 1, y: 0, scale: [0.85, 1.08, 1] }
            }
            transition={{ duration: 0.45 }}
          >
            <span>{chainActive ? 'CHAIN REACTION' : 'LANDING SQUARE'}</span>
            <strong
              style={{
                '--square-color': getBoardSquareColor(activeBoardSquare),
                '--landing-background-color':
                  getBoardSquareBackgroundColor(activeBoardSquare),
                '--impact-text-color':
                  getBoardSquareTextColor(activeBoardSquare),
              }}
            >
              {getBoardSquareLabel(activeBoardSquare)}
            </strong>
            {!automaticAttack && (
              <small>
                {formatRoll(activeBoardStep?.before ?? initialRoll)} →{' '}
                {formatRoll(activeBoardStep?.value ?? boardAdjustedRoll)}
              </small>
            )}
          </motion.div>
        )}
      {!combat && revealed && treasureRevealStage === 1 && treasureRelic && (
        <BoardTreasureReveal
          key={`${treasureRewardIndex}-${boardTreasureReward.id}`}
          reward={boardTreasureReward}
          stage={treasureRevealStage}
          reduceMotion={reduceMotion}
          onComplete={() => {
            if (treasureRewardIndex + 1 < treasureRewards.length) {
              setTreasureRewardIndex((index) => index + 1);
              setTreasureRevealStage(0);
            } else setTreasureRevealStage(2);
          }}
          activationText={
            treasureActivation
              ? `ACTIVATED THIS ROLL · +${treasureActivation.bonus}`
              : undefined
          }
        />
      )}
      {setupMode || treasureCharging ? null : automaticAttack &&
        (combat || (revealed && effectsReady)) ? null : revealed &&
        effectsReady ? (
        <div className="coreboundRollOutcomeOverlay">
          <motion.section
            className={`coreboundRollOutcome ${
              introThrow
                ? 'coreboundRollIntro'
                : passed || encounter?.boss
                ? 'coreboundRollPassed'
                : 'coreboundRollFailed'
            }`}
            aria-live="polite"
            initial={reduceMotion ? false : { opacity: 0, y: 18, scale: 0.94 }}
            animate={reduceMotion ? {} : { opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.55, ease: 'easeOut' }}
          >
            <div className="coreboundRollOutcomeHeader">
              <span className="coreboundRollOutcomeKicker">
                {introThrow
                  ? 'THE BREACH // STARTER THROW'
                  : 'SIGNAL ANALYSIS // COMPLETE'}
              </span>
              <strong
                className="coreboundRollOutcomeStatus"
                data-text={outcomeStatus}
              >
                {outcomeStatus}
              </strong>
              <span>
                {introThrow
                  ? 'Your landing reward is ready. Collect it, then choose your first portal.'
                  : survival
                  ? passed
                    ? 'Your relic brought you back. You survive with 1 HP.'
                    : 'The last spark fades. Your relic could not save you.'
                  : encounter?.boss
                  ? 'Your Rift Loom is charged. Each strike reduces the Warden’s remaining HP.'
                  : passed
                  ? 'Your Rift Loom can shatter this minion. Fire to recover its loot.'
                  : 'The minion will survive and retaliate. Its encounter cache will be lost.'}
              </span>
            </div>
            {treasureRevealStage >= 2 &&
              treasureRewards.map((reward, index) => (
                <BoardTreasureReveal
                  key={`${index}-${reward.id}`}
                  reward={reward}
                  stage={2}
                  reduceMotion={reduceMotion}
                  activationText={
                    rollRelicEffects.some((effect) => effect.id === reward.id)
                      ? `ACTIVATED THIS ROLL · +${
                          rollRelicEffects.find(
                            (effect) => effect.id === reward.id
                          ).bonus
                        }`
                      : undefined
                  }
                />
              ))}
            <div className="coreboundRollBreakdown">
              <div className="coreboundRollPersonal">
                <span>Initial die roll</span>
                <strong>{formatRoll(baseRoll)}</strong>
              </div>
              {game.flipped && (
                <div className="coreboundRollBreakdownEffect">
                  <span>
                    <small>MIRROR CHIP / INITIAL ROLL</small>
                    {formatRoll(game.roll)} → {formatRoll(getBaseRoll(game))}
                  </span>
                  <strong>{formatRoll(getBaseRoll(game))}</strong>
                </div>
              )}
              {boardSteps.map((step, index) => (
                <div
                  key={`${index}:${step.index}`}
                  className="coreboundRollBreakdownEffect coreboundRollBoardEffect"
                >
                  <span>
                    <small>
                      {chainActive
                        ? `CHAIN REACTION ${index + 1} / ${boardSteps.length}`
                        : 'BOARD SQUARE'}
                    </small>
                    {getBoardSquareLabel(step.square)}
                  </span>
                  <strong>
                    {boardSteps.length > 1
                      ? `${formatRoll(step.before)} → ${formatRoll(step.value)}`
                      : getBoardSquareImpactLabel(step.square)}
                  </strong>
                </div>
              ))}
              {rollRelicEffects.map((activation) => (
                <div
                  className="coreboundRollBreakdownEffect"
                  key={activation.id}
                >
                  <span>
                    <small>RELIC ACTIVATED</small>
                    {activation.name}
                    <em>{activation.message}</em>
                  </span>
                  <strong>+{activation.bonus}</strong>
                </div>
              ))}
              <div className="coreboundRollBreakdownBonus">
                <span>RELIC BONUSES</span>
                <strong>+{bonusTotal}</strong>
              </div>
              {!introThrow && (
                <div className="coreboundRollBreakdownTarget">
                  <span>{survival ? 'TARGET' : 'ENEMY HP'}</span>
                  <strong>
                    {room.target}
                    {survival ? '+' : ' HP'}
                  </strong>
                </div>
              )}
              <div className="coreboundRollBreakdownTotal">
                <span>
                  Final roll: <strong>{formatRoll(effectiveRoll)}</strong>
                </span>
              </div>
            </div>
            {bountyOutcome && (
              <motion.div
                className={`coreboundBountyOutcome ${
                  bountyOutcome.earned ? 'is-claimed' : ''
                }`}
                role="status"
                initial={reduceMotion ? false : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <strong>
                  {bountyOutcome.earned
                    ? `⌖ BOUNTY CLAIMED · +${bountyOutcome.earned} SHARDS`
                    : '⌖ BOUNTY TAG SPENT'}
                </strong>
                <div>{bountyOutcome.message}</div>
              </motion.div>
            )}
            {game.flipped && (
              <p className="coreboundRollModifierNote">
                Mirror Chip automatically flipped this roll.
              </p>
            )}
            <button className="coreboundPrimary" onClick={onResolve}>
              {introThrow
                ? 'Collect reward →'
                : survival
                ? passed
                  ? 'Survive at 1 HP →'
                  : 'Continue →'
                : encounter?.boss
                ? 'Strike the Warden →'
                : 'Fire Rift Loom →'}
            </button>
          </motion.section>
        </div>
      ) : revealed && mirrorActivationPending ? (
        <p className="coreboundBoardScoring" role="status">
          Mirror Chip is reversing the initial die roll to the higher value…
        </p>
      ) : revealed && (!mirrorApplied || !treasureReady) ? (
        <p className="coreboundBoardScoring" role="status">
          {!mirrorApplied
            ? 'Applying Mirror Chip to the initial die roll…'
            : 'Opening the treasure vault…'}
        </p>
      ) : revealed && !boardApplied ? (
        <p className="coreboundBoardScoring" role="status">
          The landing square is charging its effect into the die…
        </p>
      ) : revealed ? null : (
        <p className="coreboundRelicApplying" role="status">
          Signal sequencing… hold steady.
        </p>
      )}
    </div>
  );
};

export default RollReveal;
